<template>
  <div class="stripe">
    <div class="stripe__form">
      <div class="stripe__form-card">
        <div
          class="stripe__form-card-field stripe__form-card-field--card"
        ></div>
      </div>

      <ul class="stripe__form-error-list">
        <li id="card-number-error" class="stripe__form-error-item"></li>
        <li id="card-expiration-error" class="stripe__form-error-item"></li>
        <li id="card-cvc-error" class="stripe__form-error-item"></li>
      </ul>
    </div>

    <ul class="stripe__payment-method-list">
      <li
        v-for="(label, key) in PAYMENT_METHODS"
        :key="key"
        class="stripe__payment-method-item"
      >
        <img
          :src="`../../../../images/payment-methods/${key}.svg`"
          :alt="label"
          class="stripe__payment-method-item-image"
        />
      </li>
    </ul>
  </div>
</template>

<script>
import { loadStripe } from '@stripe/stripe-js';
import axios from 'axios';
import CheckoutPayment from '../../mixins/CheckoutPayment';
import Loader from '../../mixins/Loader';

const PAYMENT_METHODS = {
  visa: 'Visa',
  mastercard: 'MasterCard',
  'visa-elec': 'Visa Electron',
  amex: 'American Express',
};

const style = {
  base: {
    color: '#333333',
    fontWeight: 600,
    fontFamily: 'Montserrat, sans-serif',
    fontSize: '16px',
    fontSmoothing: 'antialiased',
    ':focus': {
      color: '#707070',
    },
    '::placeholder': {
      color: '#C7C7C7',
    },
    ':focus::placeholder': {
      color: '#666666',
    },
  },
  invalid: {
    color: '#333333',
    ':focus': {
      color: '#df332c',
    },
    '::placeholder': {
      color: '#df332c',
    },
  },
  onBehalfOf: true,
  preferredNetwork: ['cartes_bancaires', 'visa', 'mastercard'],
  hideIcon: false,
};

let stripe = null;

let cardElement = null;

let form = null;

export default {
  name: 'Stripe',
  mixins: [CheckoutPayment, Loader],
  props: {
    publishableKey: {
      type: String,
      required: true,
    },
    postcode: {
      type: String,
      required: true,
    },
    formId: {
      type: String,
      required: true,
    },
    paymentIntentUrl: {
      type: String,
      required: true,
    },
    getTokenUrl: {
      type: String,
      required: true,
    },
    errorLogUrl: {
      type: String,
      required: true,
    },
  },
  data() {
    return {
      PAYMENT_METHODS,
      stripe: null,
    };
  },
  async mounted() {
    form = document.getElementById(this.formId);

    stripe = await loadStripe(this.publishableKey);

    await this.createElements();
    this.addFormSubmitListener();
  },
  beforeDestroy() {
    this.destroyElements();
  },
  methods: {
    async createElements() {
      const elements = stripe.elements({
        fonts: [
          {
            cssSrc: 'https://fonts.googleapis.com/css?family=Montserrat',
          },
        ],
      });

      cardElement = elements.create('card', { style, hidePostalCode: true });
      cardElement.mount('.stripe__form-card-field--card');

      window.cardNumber = cardElement;

      [
        {
          element: cardElement,
          errorBlockId: 'card-number-error',
        },
      ].forEach(object => {
        object.element.addEventListener('change', event => {
          document.getElementById(object.errorBlockId).textContent = event.error
            ? event.error.message
            : '';
        });
      });
    },
    addFormSubmitListener() {
      document
        .getElementById(this.formId)
        .addEventListener('submit', this.onFormSubmit);
    },
    onFormSubmit(event) {
      const input = document.querySelector('.checkout-input__input:checked');

      if ('stripe' !== input.value) {
        return;
      }

      event.preventDefault();

      if (!this.reportFormValidity()) {
        return;
      }

      this.setLoader();
      this.getPaymentIntent()
        .then(clientSecret => this.confirmCardPayment(clientSecret))
        .then(response => this.getToken(response))
        .then(() => form.submit())
        .catch(error => {
          this.setLoader(false);

          const message =
            "Une erreur s'est produite, veuillez retenter votre paiement. Si l'erreur persiste, merci de contacter l'administrateur du site.";

          window.flashMessages = [
            {
              type: 'alert-error',
              message,
            },
          ];
          window.addToastMessages();

          Object.defineProperty(Error.prototype, 'toJSON', {
            value() {
              const alt = {};

              Object.getOwnPropertyNames(this).forEach(
                key => (alt[key] = this[key]),
                this
              );

              return alt;
            },
            configurable: true,
            writable: true,
          });

          const errorLog = error.toJSON();
          errorLog.response = error.response;

          axios.post(this.errorLogUrl, {
            _token: this.getCsrfToken(),
            error: JSON.stringify(errorLog),
          });

          if (
            error.response &&
            error.response.data &&
            error.response.data.redirect_url
          ) {
            window.location.href = error.response.data.redirect_url;
          }
        });
    },
    /**
     * @returns {Promise<AxiosResponse<any>>}
     */
    getPaymentIntent() {
      return axios.get(this.paymentIntentUrl).then(response => {
        if ('undefined' === typeof response.data.clientSecret) {
          const error = new Error('Missing client secret');
          error.debugData = response;
          throw error;
        }

        return response.data.clientSecret;
      });
    },
    /**
     * @param {string} clientSecret
     * @returns {Promise}
     */
    confirmCardPayment(clientSecret) {
      return stripe.confirmCardPayment(clientSecret, {
        payment_method: {
          card: cardElement,
          billing_details: {
            address: {
              postal_code: this.postcode,
            },
          },
        },
      });
    },
    /**
     * @param {Object} result
     * @returns {Promise<AxiosResponse<any>>}
     */
    getToken(result) {
      if (
        'undefined' === typeof result ||
        'undefined' === typeof result.paymentIntent
      ) {
        const error = new Error('Malformed response received from Stripe');
        error.debugData = result;
        throw error;
      }

      return axios.post(this.getTokenUrl, {
        _token: this.getCsrfToken(),
        paymentMethod: result.paymentIntent.payment_method,
      });
    },
    getCsrfToken() {
      return document.querySelector('meta[name="csrf-token"]').content;
    },
    destroyElements() {
      form.removeEventListener('submit', this.onFormSubmit);

      cardElement.destroy();
    },
  },
};
</script>
